import { supabase } from "@/integrations/supabase/client";
import { ApiMonitorConfig, ApiCheckLog } from "@/types";

/**
 * UptimeWing V2 Zero-Cost Architecture - Direct Supabase Client
 * Replaces the custom Node.js backend to achieve $0 scaling cost via native Edge access.
 */

export const WORKER_URL = import.meta.env.VITE_WORKER_URL || "https://uptimewing-worker.consenses.workers.dev";

// PLANS config removed - limits are now hardcoded directly as fallbacks

class APIClient {
    // --------------------------------------------------
    // Profiles & SaaS
    // --------------------------------------------------
    async getProfile() {
        try {
            const { data: authData } = await supabase.auth.getUser();
            if (!authData.user) throw new Error("Unauthorized");

            // Attempt to fetch profile with all expected columns
            let { data, error } = await (supabase as any)
                .from("profiles")
                .select("*")
                .eq("user_id", authData.user.id)
                .maybeSingle();

            if (error) {
                console.warn("PROFILE_FETCH_STRICT_FAILED: Retrying with minimal schema.", error);
                // Fallback: Fetch only the ID if the wide select failed (schema mismatch)
                const { data: minimalData, error: minError } = await (supabase as any)
                    .from("profiles")
                    .select("id, user_id, updated_at")
                    .eq("user_id", authData.user.id)
                    .maybeSingle();
                
                if (minError) throw new Error(minError.message);
                data = minimalData;
            }

            // Create profile if missing
            if (!data) {
                const newProfileData: any = { 
                    user_id: authData.user.id, 
                    email: authData.user.email 
                };

                // Only add columns we suspect might exist or use a safe insert
                try {
                    const { data: inserted, error: insertError } = await (supabase as any)
                        .from("profiles")
                        .insert([newProfileData])
                        .select()
                        .single();
                    
                    if (insertError) {
                        console.error("PRIMARY_INSERT_FAILED: Falling back to minimal profile.", insertError);
                        return { ...newProfileData, max_monitors_limit: 1, user_mode: 'common' };
                    }
                    data = inserted;

                    // Notify Admin Bot of new signup
                    fetch(`${WORKER_URL}/api/admin/notify/signup`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: newProfileData.email, userId: newProfileData.user_id })
                    }).catch(err => console.error("Admin signup notification failed", err));
                } catch (e) {
                    return { ...newProfileData, max_monitors_limit: 1, user_mode: 'common' };
                }
            }

            // Polyfill: Inject defaults for missing DB columns to keep UI happy
            const profile = {
                max_monitors_limit: 1,
                user_mode: 'common',
                ...data
            };

            // Check localStorage for user_mode override (from previous failures)
            const localMode = localStorage.getItem(`UptimeWing_user_mode_${authData.user.id}`);
            if (localMode) {
                profile.user_mode = localMode;
            }

            return profile;
        } catch (err) {
            console.error("PROFILE_CRITICAL_FAILURE", err);
            // Absolute fallback to prevent App crash
            return { user_mode: 'common', max_monitors_limit: 1 };
        }
    }

    async updateProfile(updates: any) {
        try {
            const { data: authData } = await supabase.auth.getUser();
            if (!authData.user) throw new Error("Unauthorized");

            const { data, error } = await (supabase as any)
                .from("profiles")
                .update({ ...updates })
                .eq("user_id", authData.user.id)
                .select()
                .single();

            if (error) {
                console.warn("PROFILE_UPDATE_WARNING: Database check failed, falling back to local state.", error);
                
                // Polyfill for missing columns: Store user_mode in localStorage if DB update fails 
                if (updates.user_mode) {
                    localStorage.setItem(`UptimeWing_user_mode_${authData.user.id}`, updates.user_mode);
                }
                
                // Silent fallback for schema mismatch
                if (error.code === "PGRST204" || error.message.includes("column") || error.message.includes("schema cache")) {
                    return { ...updates, user_id: authData.user.id };
                }
                console.error("PROFILE_UPDATE_ERROR (CRITICAL)", error);
                throw new Error(error.message);
            }
            return data;
        } catch (err) {
            // Only log if it's not a handled schema mismatch
            if (!(err instanceof Error && (err.message.includes("column") || err.message.includes("schema cache")))) {
                console.error("PROFILE_UPDATE_ERROR", err);
            }
            throw err;
        }
    }

    // --------------------------------------------------
    // Sites
    // --------------------------------------------------
    async getSites() {
        try {
            const { data: user } = await supabase.auth.getUser();
            if (!user.user) throw new Error("Unauthorized");

            // Attempt direct websites table read
            const { data, error } = await supabase
                .from("websites")
                .select("*")
                .eq("user_id", user.user.id);

            if (!error && data) {
                return data as any[];
            }

            // Fallback to AssetStore if tables/columns are configured differently
            const { SupabaseAssetStore } = await import("../../packages/infra/supabase/AssetStore");
            const assetStore = new SupabaseAssetStore(supabase);
            const assets = await assetStore.findByUser(user.user.id);
            return assets.map(a => ({
                id: a.id,
                url: a.url,
                name: a.name,
                created_at: a.created_at,
                user_id: a.user_id,
                source_type: a.source_type
            }));
        } catch (err) {
            console.error("FLEET_FETCH_ERROR", err);
            // Graceful recovery for dashboard UI integrity
            return [];
        }
    }

    async getSite(id: string) {
        try {
            const { data: user } = await supabase.auth.getUser();
            if (!user.user) throw new Error("Unauthorized");

            const { data, error } = await (supabase as any)
                .from("websites")
                .select("*")
                .eq("id", id)
                .eq("user_id", user.user.id)
                .single();

            if (error) throw new Error(error.message);
            return data as any;
        } catch (err) {
            console.error("SITE_DETAIL_FETCH_ERROR", err);
            throw err;
        }
    }

    async getPublicSite(idOrSlug: string) {
        try {
            const { data, error } = await supabase.functions.invoke("public-status", {
                method: "POST",
                body: { id: idOrSlug }
            });

            if (error) throw new Error(error.message);
            if (!data || !data.site) throw new Error("Not Found or Private");
            
            return { site: data.site, logs: data.logs || [], incidents: data.incidents || [] };
        } catch (err) {
            console.error("PUBLIC_SITE_FETCH_ERROR", err);
            throw err; // Expected to throw if site is private or doesn't exist
        }
    }

    async createSite(site: {
        name: string | null;
        url: string;
        check_interval_minutes: number;
        favicon_url?: string;
        type?: 'url' | 'heartbeat';
        grace_period_minutes?: number;
        keyword?: string | null;
        verification_token?: string | null;
        monitor_type?: 'website' | 'api';
    }) {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        const profile = await this.getProfile();
        const sites = await this.getSites();

        if (sites.length >= (profile.site_limit || 5)) {
            throw new Error(`Limit reached. Free users can only monitor ${profile.site_limit || 5} sites.`);
        }

        // ── STANDALONE SPAM HOLE PATCH ─────────────────────────────────────
        const isStandalone = !site.monitor_type || site.monitor_type === 'website' || site.type === 'url';
        if (isStandalone) {
            if (!site.url.startsWith("https://")) {
                throw new Error("Outage diagnosis is restricted to secure HTTPS endpoints only.");
            }
            if (site.url.includes("*")) {
                throw new Error("Wildcard paths (*) are disabled on standard monitor nodes.");
            }
            // Check count of active standalone checks
            const standaloneCount = sites.filter(s => s.type === 'url' || !s.monitor_type).length;
            if (standaloneCount >= (profile.site_limit || 5)) {
                throw new Error(`Out of checks. Your plan limits standalone checks to ${profile.site_limit || 5} endpoints.`);
            }
        }

        const verificationToken = site.verification_token || crypto.randomUUID();
        const heartbeatToken = site.type === 'heartbeat' ? crypto.randomUUID().split('-')[0] : null;

        const { data, error } = await (supabase as any)
            .from("websites")
            .insert([{
                ...site,
                user_id: user.user.id,
                verification_token: verificationToken,
                heartbeat_token: heartbeatToken,
                is_verified: (site.type === 'heartbeat' || site.monitor_type === 'api') ? true : false,
                is_public: false,
                type: site.type || 'url',
                grace_period_minutes: site.grace_period_minutes || 10,
                monitoring_type: isStandalone ? 'standalone' : 'pagerduty'
            }])
            .select()
            .single();

        if (error) throw new Error(error.message);


        // Notify Admin Bot of new site deployed
        fetch(`${WORKER_URL}/api/admin/notify/site-added`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                name: data.name,
                url: data.url,
                type: data.type,
                email: user.user.email
            })
        }).catch(err => console.error("Admin site added notification failed", err));

        // Point 3 Auto-Resolution: Trigger initial forensic audits immediately
        if (data && data.type === 'url') {
            console.log("Triggering initial tactical audits for:", data.url);
            supabase.functions.invoke(`site-audit`, { method: 'POST', body: { website_id: data.id } }).catch(() => { });
            supabase.functions.invoke(`refresh-check`, { method: 'POST', body: { website_id: data.id, check_type: 'ssl' } }).catch(() => { });
        }

        return data;
    }

    async verifySite(url: string) {
        const { data, error } = await supabase.functions.invoke("live-check", {
            method: "POST",
            body: { url },
        });

        if (error) throw new Error(error.message);
        return { ...data, verified: data?.status === 'up' };
    }

    async verifyOwnership(siteId: string, method: string = "meta") {
        const { data, error } = await supabase.functions.invoke("verify-domain", {
            method: "POST",
            body: { websiteId: siteId, method },
        });

        if (error) throw new Error(error.message);

        // Update local cache if verified
        if (data?.success) {
            const { error: updateError } = await (supabase as any)
                .from("websites")
                .update({ is_verified: true, verification_method: method })
                .eq("id", siteId);
            
            if (updateError && (updateError.message.includes("verification_method") || updateError.message.includes("column"))) {
                await (supabase as any)
                    .from("websites")
                    .update({ is_verified: true })
                    .eq("id", siteId);
            }
        }

        return data;
    }

    async updateSite(id: string, updates: any) {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        const { data, error } = await (supabase as any)
            .from("websites")
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq("id", id)
            .eq("user_id", user.user.id)
            .select()
            .single();

        if (error) throw new Error(error.message);
        return data;
    }

    async deleteSite(id: string) {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        const { error } = await (supabase as any)
            .from("websites")
            .delete()
            .eq("id", id)
            .eq("user_id", user.user.id);

        if (error) throw new Error(error.message);
    }

    async getLogs(siteId?: string, since?: string, limit?: number) {
        let query = (supabase as any).from("check_logs").select("*");
        if (siteId) query = query.eq("website_id", siteId);
        if (since) query = query.gte("checked_at", since);

        const { data, error } = await query
            .order("checked_at", { ascending: false })
            .limit(limit || 2000);

        if (error) throw new Error(error.message);
        return data || [];
    }

    async getIncidents(websiteId?: string, limit = 50) {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        // Atlas v1: Using EventStore port with LegacyAdapter feature flags
        if (websiteId) {
            const { SupabaseEventStore } = await import("../../packages/infra/supabase/EventStore");
            const { SupabaseFeatureFlags } = await import("../../packages/infra/supabase/FeatureFlags");
            const { LegacyAdapter } = await import("../../packages/infra/LegacyAdapter");
            
            const eventStore = new SupabaseEventStore(supabase);
            const featureFlags = new SupabaseFeatureFlags(supabase);
            const legacyAdapter = new LegacyAdapter(supabase, featureFlags, eventStore);

            const events = await legacyAdapter.getEvents(websiteId, user.user.id);
            return events.map((e: any) => ({
                id: e.id,
                website_id: e.asset_id || e.website_id,
                status: e.status,
                started_at: e.occurred_at || e.checked_at,
                created_at: e.occurred_at || e.checked_at,
                response_time_ms: e.response_time_ms
            })).slice(0, limit);
        }

        let query = (supabase as any)
            .from("incidents")
            .select("*, websites!inner(user_id, name, url)")
            .eq("websites.user_id", user.user.id);

        const { data, error } = await query
            .order("started_at", { ascending: false })
            .limit(limit);

        if (error) throw new Error(error.message);
        return data || [];
    }

    async getOngoingIncidents() {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        // Atlas v1: Using IncidentStore port
        const { SupabaseIncidentStore } = await import("../../packages/infra/supabase/IncidentStore");
        const incidentStore = new SupabaseIncidentStore(supabase);
        // Find open incidents by fetching assets for user first
        const { SupabaseAssetStore } = await import("../../packages/infra/supabase/AssetStore");
        const assetStore = new SupabaseAssetStore(supabase);
        const assets = await assetStore.findByUser(user.user.id);
        const assetIds = assets.map(a => a.id);

        const allOpenIncidents: any[] = [];
        for (const assetId of assetIds) {
            const incs = await incidentStore.findOpenByAsset(assetId);
            const asset = assets.find(a => a.id === assetId);
            incs.forEach(i => {
                allOpenIncidents.push({
                    id: i.id,
                    website_id: i.asset_id,
                    status: i.status,
                    severity: i.severity,
                    created_at: i.created_at,
                    resolved_at: i.resolved_at,
                    started_at: i.created_at,
                    websites: {
                        user_id: user.user.id,
                        name: asset?.name,
                        url: asset?.url
                    }
                });
            });
        }
        return allOpenIncidents;
    }

    async resolveIncident(incidentId: string) {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        const resolvedAt = new Date().toISOString();
        const { data: incident, error: getError } = await (supabase as any)
            .from("incidents")
            .select("started_at, website_id")
            .eq("id", incidentId)
            .single();

        if (getError) throw new Error(getError.message);

        const durationSeconds = incident
            ? Math.floor((new Date().getTime() - new Date(incident.started_at).getTime()) / 1000)
            : 0;

        const { error: updateError } = await (supabase as any)
            .from("incidents")
            .update({
                status: "resolved",
                resolved_at: resolvedAt,
                duration_seconds: durationSeconds
            })
            .eq("id", incidentId);

        if (updateError) throw new Error(updateError.message);

        if (incident?.website_id) {
            await (supabase as any)
                .from("websites")
                .update({ status: "up" })
                .eq("id", incident.website_id);
        }
    }

    async updateIncidentStatus(incidentId: string, newStatus: string) {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        const updatePayload: any = {
            status: newStatus
        };

        if (newStatus === "resolved") {
            updatePayload.resolved_at = new Date().toISOString();
        } else {
            updatePayload.resolved_at = null;
        }

        const { error: updateError } = await (supabase as any)
            .from("incidents")
            .update(updatePayload)
            .eq("id", incidentId);

        if (updateError) throw new Error(updateError.message);

        // If marked resolved, update corresponding website status if needed
        if (newStatus === "resolved") {
            const { data: incident } = await (supabase as any)
                .from("incidents")
                .select("website_id")
                .eq("id", incidentId)
                .single();
            if (incident?.website_id) {
                await (supabase as any)
                    .from("websites")
                    .update({ status: "up" })
                    .eq("id", incident.website_id);
            }
        } else if (newStatus === "ongoing") {
            const { data: incident } = await (supabase as any)
                .from("incidents")
                .select("website_id")
                .eq("id", incidentId)
                .single();
            if (incident?.website_id) {
                await (supabase as any)
                    .from("websites")
                    .update({ status: "down" })
                    .eq("id", incident.website_id);
            }
        }
    }

    private settingsPromise: Promise<any> | null = null;

    async getSettings() {
        if (this.settingsPromise) {
            return this.settingsPromise;
        }

        this.settingsPromise = (async () => {
            try {
                const { data: user } = await supabase.auth.getUser();
                if (!user.user) throw new Error("Unauthorized");

                const { data, error } = await (supabase as any)
                    .from("notification_settings")
                    .select("*")
                    .eq("user_id", user.user.id)
                    .maybeSingle();

                if (error) throw new Error(error.message);

                if (!data) {
                    // Auto-create/populate defaults in DB so background check runners can send emails immediately
                    const defaultSettings = {
                        user_id: user.user.id,
                        email_enabled: true,
                        email_to_address: user.user.email,
                        notify_on_down: true,
                        notify_on_recovery: true,
                        notify_on_ssl_expiry: true,
                        discord_enabled: false,
                        telegram_enabled: false,
                        slack_enabled: false,
                        vercel_enabled: false,
                        updated_at: new Date().toISOString()
                    };

                    try {
                        const { data: inserted, error: insertError } = await (supabase as any)
                            .from("notification_settings")
                            .insert(defaultSettings)
                            .select()
                            .single();
                            
                        if (insertError) {
                            if (insertError.code === '23505' || insertError.message?.includes("duplicate key")) {
                                const { data: secondTry } = await (supabase as any)
                                    .from("notification_settings")
                                    .select("*")
                                    .eq("user_id", user.user.id)
                                    .maybeSingle();
                                if (secondTry) return secondTry;
                            }
                            if (insertError.code === '23503') {
                                return defaultSettings;
                            }
                            console.debug("[UptimeWing] Auto-create settings failed:", insertError.code);
                            return defaultSettings;
                        }
                        return inserted;
                    } catch {
                        // Fallback check
                        const { data: finalTry } = await (supabase as any)
                            .from("notification_settings")
                            .select("*")
                            .eq("user_id", user.user.id)
                            .maybeSingle();
                        return finalTry || defaultSettings;
                    }
                }

                // If record exists, ensure email_enabled defaults to true if not explicitly false
                if (data.email_enabled === null || data.email_enabled === undefined) {
                    data.email_enabled = true;
                }
                if (!data.email_to_address) {
                    data.email_to_address = user.user.email;
                }

                return data;
            } finally {
                this.settingsPromise = null;
            }
        })();

        return this.settingsPromise;
    }

    async updateSettings(updates: any) {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        const { data, error } = await (supabase as any)
            .from("notification_settings")
            .upsert({ 
                ...updates, 
                user_id: user.user.id,
                updated_at: new Date().toISOString() 
            }, { onConflict: "user_id" })
            .select()
            .single();

        if (error) throw new Error(error.message);
        return data;
    }

    async getAudits(websiteId: string) {
        const { data, error } = await (supabase as any)
            .from("site_audits")
            .select("*")
            .eq("website_id", websiteId)
            .order("checked_at", { ascending: false });

        if (error) throw new Error(error.message);
        return data || [];
    }

    async getDnsRecords(websiteId: string) {
        const { data, error } = await (supabase as any)
            .from("dns_records")
            .select("*")
            .eq("website_id", websiteId)
            .order("checked_at", { ascending: false });

        if (error) throw new Error(error.message);
        return data || [];
    }

    async getGithubRepositories() {
        const { data, error } = await supabase.functions.invoke("github-helper", {
            method: "POST",
            body: { action: "list-repos" },
        });
        if (error) throw new Error(error.message);
        return data?.repositories || [];
    }

    async getGithubWorkflows(owner: string, repo: string) {
        const { data, error } = await supabase.functions.invoke("github-helper", {
            method: "POST",
            body: { action: "list-workflows", owner, repo },
        });
        if (error) throw new Error(error.message);
        return data?.workflows || [];
    }

    async getApiMonitorConfig(websiteId: string): Promise<ApiMonitorConfig | null> {
        const { data, error } = await supabase
            .from("api_monitor_config")
            .select("*")
            .eq("website_id", websiteId)
            .maybeSingle();

        if (error) throw new Error(error.message);
        return data as ApiMonitorConfig | null;
    }

    async saveApiMonitorConfig(config: Omit<ApiMonitorConfig, 'id' | 'created_at' | 'updated_at'>) {
        const { data: existing } = await supabase
            .from("api_monitor_config")
            .select("id")
            .eq("website_id", config.website_id)
            .maybeSingle();

        if (existing) {
            const { data, error } = await supabase
                .from("api_monitor_config")
                .update(config)
                .eq("website_id", config.website_id)
                .select()
                .single();
            if (error) throw new Error(error.message);
            return data;
        } else {
            const { data, error } = await supabase
                .from("api_monitor_config")
                .insert([config])
                .select()
                .single();
            if (error) throw new Error(error.message);
            return data;
        }
    }

    async getApiLogs(siteId?: string, since?: string, limit?: number): Promise<ApiCheckLog[]> {
        let query = supabase.from("api_check_logs").select("*");
        if (siteId) query = query.eq("website_id", siteId);
        if (since) query = query.gte("checked_at", since);

        const { data, error } = await query
            .order("checked_at", { ascending: false })
            .limit(limit || 2000);

        if (error) throw new Error(error.message);
        return (data as ApiCheckLog[]) || [];
    }

    // Slack OAuth Integrations
    async getSlackChannels() {
        return this.request<any[]>("/api/slack/channels");
    }

    async disconnectSlack() {
        return this.request<any>("/api/slack/disconnect", { method: "POST" });
    }

    async testSlack() {
        return this.request<any>("/api/slack/test", { method: "POST" });
    }

    // Generic Webhooks
    async getWebhooks() {
        return this.request<any[]>("/api/webhooks");
    }

    async createWebhook(webhook: { name: string; url: string; secret_raw?: string | null; events: string[]; enabled: boolean }) {
        return this.request<any>("/api/webhooks", {
            method: "POST",
            body: JSON.stringify(webhook)
        });
    }

    async updateWebhook(id: string, webhook: Partial<{ name: string; url: string; secret_raw: string | null; events: string[]; enabled: boolean }>) {
        return this.request<any>(`/api/webhooks/${id}`, {
            method: "PUT",
            body: JSON.stringify(webhook)
        });
    }

    async deleteWebhook(id: string) {
        return this.request<any>(`/api/webhooks/${id}`, { method: "DELETE" });
    }

    async getWebhookDeliveries(id: string) {
        return this.request<any[]>(`/api/webhooks/${id}/deliveries`);
    }

    async testWebhook(id: string) {
        return this.request<any>(`/api/webhooks/${id}/test`, { method: "POST" });
    }

    async retryWebhookDelivery(id: string, deliveryId: string) {
        return this.request<any>(`/api/webhooks/${id}/retry/${deliveryId}`, { method: "POST" });
    }

    // Service Registry & Alert Mapping
    async getServices() {
        try {
            const { data: user } = await supabase.auth.getUser();
            if (!user.user) throw new Error("Unauthorized");

            const { data, error } = await supabase
                .from("services")
                .select("*")
                .eq("user_id", user.user.id);

            if (error) throw error;
            return data || [];
        } catch (err) {
            console.error("Failed to fetch services registry details", err);
            return [];
        }
    }

    async createService(name: string) {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error("Unauthorized");

        const { data, error } = await supabase
            .from("services")
            .insert([{ name, user_id: user.user.id }])
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    async getServiceSources(serviceId: string) {
        const { data, error } = await supabase
            .from("service_sources")
            .select("*")
            .eq("service_id", serviceId);

        if (error) throw error;
        return data || [];
    }

    async addServiceSource(serviceId: string, sourceType: string, sourceId: string, config: any = {}) {
        const { data, error } = await supabase
            .from("service_sources")
            .insert([{ service_id: serviceId, source_type: sourceType, source_id: sourceId, config }])
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    async deleteServiceSource(serviceId: string, sourceId: string) {
        const { data, error } = await supabase
            .from("service_sources")
            .delete()
            .eq("service_id", serviceId)
            .eq("id", sourceId);

        if (error) throw error;
        return data;
    }

    async deleteService(serviceId: string) {
        const { data, error } = await supabase
            .from("services")
            .delete()
            .eq("id", serviceId);

        if (error) throw error;
        return data;
    }

    async getServiceStatus(serviceId: string) {
        return this.request<any>(`/api/services/${serviceId}/status`);
    }

    async getServiceTimeline(serviceId: string) {
        return this.request<any[]>(`/api/services/${serviceId}/timeline`);
    }

    async request<T>(path: string, options: any = {}): Promise<T> {
        const url = `${WORKER_URL}${path}`;
        
        let authHeader = {};
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) {
                authHeader = { "Authorization": `Bearer ${session.access_token}` };
            }
        } catch (e) {
            console.error("Auth header generation failed", e);
        }

        const response = await fetch(url, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                ...authHeader,
                ...(options.headers || {}),
            },
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({ message: "Unknown error" })) as any;
            throw new Error(err.message || "Request failed");
        }

        return await response.json();
    }
}

export const api = new APIClient();
